<script setup>
// ──────────────────────────────────────────────
// Imports
// ──────────────────────────────────────────────
import { openModal } from '@kolirt/vue-modal';
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useCustomerStore } from '@/stores/customer';
import { useCasinoStoreV2 } from '@/stores/casino_v2';
import { useGeneralStore } from '@/stores/general';
import { useInitializeStore } from '@/stores/initialize';
import { useCurrenciesStore } from '@/stores/currencies';

import Login from '@/components/modals/Login.vue';
import Register from '@/components/modals/Register.vue';
import VerificationEmail from '@/components/modals/VerificationEmail.vue';
import GameOverlayModal from '@/components/casino_v2/game/GameOverlayModal.vue';

// ──────────────────────────────────────────────
// Store/Router Setup
// ──────────────────────────────────────────────
const router = useRouter();
const customerStore = useCustomerStore();
const casinoStore = useCasinoStoreV2();
const generalStore = useGeneralStore();
const initializeStore = useInitializeStore();
const currenciesStore = useCurrenciesStore();

// ──────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────
const props = defineProps({
	game: { type: Object, required: true },
	size: { type: String, default: 'small' },
	force_opacity: { type: Boolean, default: false },
	aspect_ratio: { type: String, default: 'wide' },
});

const statistics = computed(() => {
	if (props.game.statistics) return JSON.parse(props.game.statistics);

	return null;
});

// ──────────────────────────────────────────────
// Modal & Overlay Handlers
// ──────────────────────────────────────────────

/**
 * Show full game overlay as modal (for mobile).
 * @param {string} image_url
 */
const showOverlayModal = (image_url) => {
	openModal(GameOverlayModal, { game: props.game, image_url }).catch(() => { });
};

/**
 * Show registration modal. If "Go to Login" requested, chain login modal.
 */
const showRegisterModal = () => {
	openModal(Register)
		.then((data) => { if (data.show_login) showLoginModal(); })
		.catch(() => { });
};

/**
 * Show login modal. If "Go to Register" requested, chain register modal.
 */
const showLoginModal = () => {
	openModal(Login)
		.then((data) => { if (data.show_register) showRegisterModal(); })
		.catch(() => { });
};

/**
 * Show verification email modal.
 */
const showVerificationEmailModal = () => {
	openModal(VerificationEmail).catch(() => { });
};

// ──────────────────────────────────────────────
// Image Handling & Error Management
// ──────────────────────────────────────────────

const has_error = ref(false);

/**
 * Triggered if image fails to load.
 */
const setError = () => { has_error.value = true; };

/** True if image is broken or missing. */
const hasError = computed(() => has_error.value);

/**
 * Compute game image URL, fallback to default if broken or invalid.
 * Applies license rules and validates extension.
 */
const imageUrl = computed(() => {
	if (generalStore.isLicenseEnabled === 'false') return props.game.image_url;
	let url = props.game.image_url;

	if (props.game['dealer_avatar'] && props.game['dealer_avatar'] !== '') {
		url = props.game['dealer_avatar'];
	}

	// Use fallback if URL is invalid or file not image
	if (!url || !isImageUrlValid.value) {
		return '/assets/images/brand_logo.svg';
	}

	return url;
});

/**
 * True if fallback/default image should be shown.
 */
const isDefaultImage = computed(() =>
	imageUrl.value === '/assets/images/brand_logo.svg' || has_error.value
);

/**
 * Validate image URL format and file extension.
 */
const isImageUrlValid = computed(() => {
	let url = props.game.image_url;
	let url_valid = true;

	if (!url) {
		url_valid = false;
	} else {
		// Validate filename and extension
		if (url_valid) {
			const fileName = url.split('/').pop();
			if (!fileName || fileName.includes('..')) url_valid = false;
			const validImageRegex = /\.(jpeg|jpg|png|svg|gif|webp)$/i;
			if (!validImageRegex.test(fileName)) url_valid = false;
		}
	}
	return url_valid;
});

/**
 * Lazy load image when visible (for IntersectionObserver).
 * @param {boolean} is_visible
 * @param {object} entry
 */
const visibilityChanged = (is_visible, entry) => {
	if (is_visible) {
		const img_element = entry.target;
		const data_src = img_element.getAttribute('data-src');
		if (data_src) {
			img_element.setAttribute('src', data_src);
			img_element.removeAttribute('data-src');
		}
	}
};

// ──────────────────────────────────────────────
// Game Actions: Play, Demo, Favourites
// ──────────────────────────────────────────────

/**
 * Go to game view, enforcing login/verification as needed.
 * @param {Object} game
 * @param {Boolean} demo
 */
const goToGame = (game, demo = false) => {
	const game_id = generalStore.isLicenseEnabled === 'true' ? game.id : game.game_id;
	let params = { game_id };
	if (demo) params.demo = 'demo';

	const router_object = { name: 'casino-game', params };

	// Require login
	if (!customerStore.isLoggedIn) {
		useCustomerStore().redirect_after_login = router_object;
		showLoginModal();
		return;
	}

	// Require email verification
	if (!customerStore.hasVerifiedEmail) {
		showVerificationEmailModal();
		return;
	}

	// Go to game
	router.push(router_object);
};

/**
 * CSS class for play button based on size prop.
 */
const selectPlayButtonClass = computed(() => {
	switch (props.size) {
		case 'extra-small': return 'play-button-extra-small';
		case 'small': return 'play-button-small';
		case 'large': return 'play-button-large';
		default: return 'play-button-small';
	}
});

/**
 * CSS class for demo button based on size prop.
 */
const selectDemoButtonClass = computed(() => {
	switch (props.size) {
		case 'small': return 'demo-text-small';
		case 'large': return 'demo-text-large';
		default: return 'demo-text-small';
	}
});

/**
 * CSS class for favourite container based on size prop.
 */
const selectFavContainerClass = computed(() => {
	switch (props.size) {
		case 'extra-small': return 'favourite-container-extra-small';
		case 'small': return 'favourite-container-small';
		case 'large': return 'favourite-container-large';
		default: return 'favourite-container-small';
	}
});

/**
 * CSS class for favourite SVG icon based on size prop.
 */
const selectFavClass = computed(() => {
	switch (props.size) {
		case 'extra-small': return 'fav-svg-extra-small';
		case 'small': return 'fav-svg-small';
		case 'large': return 'fav-svg-large';
		default: return 'fav-svg-small';
	}
});

/**
 * CSS class for table statistics based on size and game type.
 */
const tableStatisticsClass = computed(() => {
	if (props.size == 'small') {
		if (props.game.type && props.game.type.toLowerCase().includes('baccarat')) {
			return 'game-statistics-card-baccarat-small';
		}

		return 'game-statistics-card-small';
	}

	return 'game-statistics-card-large';
});

/**
 * Add or remove favourite. Requires login and verified email.
 * @param {Object} game
 */
const toggleFavourite = (game) => {
	if (!customerStore.isLoggedIn) {
		showLoginModal();
		return;
	}
	if (!customerStore.hasVerifiedEmail) {
		showVerificationEmailModal();
		return;
	}
	casinoStore.toggleFavourite(game.id)
		.then((has_favourites) => {
			// If no favourites left, exit the favourites page.
			if (!has_favourites && casinoStore.getActivePage == 'favourites') {
				router.back();
			}
		})
		.catch((error) => {
			console.error('Error toggling favourite:', error);
		});
};

/**
 * On mobile: show overlay for this game.
 * @param {string} image_url
 */
const openOverlay = (image_url) => {
	if (initializeStore.isMobile) {
		showOverlayModal(hasError.value ? '/assets/images/brand_logo.svg' : image_url);
	}
};

// ──────────────────────────────────────────────
// Watchers
// ──────────────────────────────────────────────

/**
 * If image changes, reset error flag.
 */
watch(() => props.game.image_url, (newVal, oldVal) => {
	if (newVal !== oldVal) has_error.value = false;
});

/**
 * Get roulette color class based on color value.
 * @param color
 */
const getRouletteColor = (color) => {
	switch (color) {
		case 'green': return 'roulette-green';
		case 'red': return 'roulette-red';
		case 'black': return 'roulette-black';
		default: return '';
	}
};

/**
 * Statistics limit based on size prop.
 * Determines how many recent numbers to show for roulette, baccarat, and blackjack.
 */
const statsLimit = computed(() => {
	switch (props.size) {
		case 'extra-small': return { roulette: 4, baccarat: 6, blackjack: 4 };
		case 'small': return { roulette: 6, baccarat: 10, blackjack: 5 };
		default: return { roulette: 10, baccarat: 15, blackjack: 7 };
	}
});

// ──────────────────────────────────────────────
// Category Ribbon (TOP / NEW badge)
// ──────────────────────────────────────────────
import ribbonTopSvg from '@/assets/images/ribbon-top.svg';
import ribbonNewSvg from '@/assets/images/ribbon-new.svg';

/**
 * Determines which ribbon badge (if any) should be displayed on this game tile.
 * Priority: 'top' wins over 'new'.
 * Returns 'top', 'new', or null.
 */
const ribbonType = computed(() => {
	if (!props.game.category_ids) return null;

	let category_ids;
	try {
		category_ids = typeof props.game.category_ids === 'string'
			? JSON.parse(props.game.category_ids)
			: props.game.category_ids;
	} catch {
		return null;
	}

	if (!Array.isArray(category_ids) || category_ids.length === 0) return null;

	const categories = casinoStore.categories;
	let has_top = false;
	let has_new = false;

	for (const catId of category_ids) {
		const cat = categories.find(c => String(c.id) === String(catId));
		if (cat) {
			if (cat.slug === 'top') has_top = true;
			if (cat.slug === 'new') has_new = true;
		}
	}

	if (has_top) return 'top';
	if (has_new) return 'new';
	return null;
});

/**
 * Returns the SVG source for the active ribbon type.
 */
const ribbonSrc = computed(() => {
	if (ribbonType.value === 'top') return ribbonTopSvg;
	if (ribbonType.value === 'new') return ribbonNewSvg;
	return null;
});
</script>
<template>
	<!-- Live Casino Game Image Card -->
	<div @click="openOverlay(imageUrl)"
		:class="['image-container', `aspect-${props.aspect_ratio}`, isDefaultImage ? 'default-image-container' : '', props.force_opacity == true ? 'default-image-container-dark' : '']">
		<!-- Main image -->
		<img v-if="!hasError" :data-src="imageUrl"
			:class="{ 'game-image-hover': true, 'default-image': isDefaultImage }" :alt="props.game.name"
			loading="lazy" decoding="async"
			@error="setError()" v-observe-visibility="visibilityChanged">
		<!-- Fallback image -->
		<img v-else src="/assets/images/brand_logo.svg" :alt="props.game.name"
			class="default-image game-image-hover">

		<!-- Category Ribbon Badge (TOP / NEW) -->
		<img v-if="ribbonSrc" :src="ribbonSrc" :alt="ribbonType" class="ribbon-badge" />

		<!-- Dealer name -->
		<div v-if="props.game['dealer_name'] && props.game['dealer_name'] != 'Auto'" class="dealer-name-container" :class="{ 'fs-9px': props.size == 'small' }">
			<span>{{ props.game['dealer_name'] }}</span>
		</div>

		<!-- Statistis -->
		<div v-if="statistics && props.size != 'extra-small'" class="game-statistics-card" :class="tableStatisticsClass">
			<!-- Roulette Stats -->
			<template v-if="props.game.type && props.game.type.toLowerCase().includes('roulette')">
				<div class="roulette-stat-block">
					<div class="roulette-numbers">
						<span v-for="item in statistics.recent_numbers?.slice(0, statsLimit.roulette)" :key="item.value"
							:class="['roulette-number', getRouletteColor(item.color)]">
							{{ item.number }}
						</span>
					</div>
				</div>
			</template>

			<!-- Baccarat Stats -->
			<template v-else-if="props.game.type && props.game.type.toLowerCase().includes('baccarat')">
				<div class="baccarat-stat-block">
					<div class="bigroad-grid">
						<div v-for="(cell, i) in statistics.big_road" :key="i" :class="[ 'bigroad-cell', cell.main == 1 ? 'banker' : '', cell.main == 2 ? 'player' : '', cell.tie ? 'tie' : '' ]"
						:style="{ left: (cell.x * 13) + 'px', top: (cell.y * 13) + 'px'	}"></div>
					</div>
				</div>
			</template>
		</div>

		<!-- Schedule -->
		<div v-if="props.game.is_open == 0 && props.game.open_hours" class="table-schedule">
			<span class="table-schedule-label">{{ $t('general.schedule') }}</span>
			<span class="table-schedule-time">{{ props.game.open_hours }}</span>
		</div>

		<!-- Limits -->
		<div v-if="game.hasOwnProperty('limit_min') && game.hasOwnProperty('limit_max')" class="game-limits"
			:class="[props.size == 'small' ? 'game-limits-small' : '']">{{ $t('general.min') }} {{
				currenciesStore.format_currency(props.game.limit_min, props.game.currency) }} {{ $t('general.max') }} {{
				currenciesStore.format_currency(props.game.limit_max, props.game.currency) }}</div>

		<!-- Overlay controls (desktop only): Favourite, Play, Demo -->
		<div v-if="!initializeStore.isMobile" class="overlay" :class="isDefaultImage ? 'overlay-light' : ''">
			<!-- Favourite star (toggles on click, filled if favourite) -->
			<div :class="selectFavContainerClass" class="favourite-container" @click.stop="toggleFavourite(props.game)">
				<svg v-if="props.game.is_favourite == true" :class="selectFavClass" xmlns="http://www.w3.org/2000/svg"
					width="12" height="11.143" viewBox="0 0 12 11.143">
					<path id="Icon_ionic-ios-star" data-name="Icon ionic-ios-star"
						d="M12.67,5.545H8.729l-1.2-3.573a.434.434,0,0,0-.814,0l-1.2,3.573H1.554a.43.43,0,0,0-.429.429.315.315,0,0,0,.008.072.412.412,0,0,0,.179.3L4.551,8.63,3.308,12.244a.43.43,0,0,0,.147.482.414.414,0,0,0,.241.1.525.525,0,0,0,.268-.1l3.161-2.253,3.161,2.253a.5.5,0,0,0,.268.1.385.385,0,0,0,.238-.1.425.425,0,0,0,.147-.482L9.7,8.63l3.212-2.3.078-.067a.449.449,0,0,0,.139-.287A.454.454,0,0,0,12.67,5.545Z"
						transform="translate(-1.125 -1.688)" fill="var(--primary)" opacity="1" />
				</svg>
				<svg v-else :class="selectFavClass" xmlns="http://www.w3.org/2000/svg" width="12" height="11.143"
					viewBox="0 0 12 11.143">
					<path id="Icon_ionic-ios-star" data-name="Icon ionic-ios-star"
						d="M12.67,5.545H8.729l-1.2-3.573a.434.434,0,0,0-.814,0l-1.2,3.573H1.554a.43.43,0,0,0-.429.429.315.315,0,0,0,.008.072.412.412,0,0,0,.179.3L4.551,8.63,3.308,12.244a.43.43,0,0,0,.147.482.414.414,0,0,0,.241.1.525.525,0,0,0,.268-.1l3.161-2.253,3.161,2.253a.5.5,0,0,0,.268.1.385.385,0,0,0,.238-.1.425.425,0,0,0,.147-.482L9.7,8.63l3.212-2.3.078-.067a.449.449,0,0,0,.139-.287A.454.454,0,0,0,12.67,5.545Z"
						transform="translate(-1.125 -1.688)" fill="var(--white)" opacity="0.5" />
				</svg>
			</div>

			<!-- Play button -->
			<button class="play-button-container">
				<div class='play-button' :class="selectPlayButtonClass" @click="goToGame(props.game)">
					<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
						x="0px" y="0px" width="213.7px" height="213.7px" viewBox="0 0 213.7 213.7"
						enable-background="new 0 0 213.7 213.7" xml:space="preserve">
						<polygon class='triangle' id="XMLID_18_" fill="none" stroke-width="7" stroke-linecap="round"
							stroke-linejoin="round" stroke-miterlimit="10" points="73.5,62.5 148.5,105.8 73.5,149.1 " />
						<circle class='circle' id="XMLID_17_" fill="none" stroke-width="7" stroke-linecap="round"
							stroke-linejoin="round" stroke-miterlimit="10" cx="106.8" cy="106.8" r="103.3" />
					</svg>
				</div>
			</button>

			<!-- Demo button -->
			<div v-if="game.has_demo == true" class="demo-text" :class="selectDemoButtonClass"
				@click="goToGame(props.game, true)">
				{{ $t('general.demo') }}
			</div>
		</div>
	</div>
</template>
<style scoped>
.default-image-container {
	/* opacity: 0.2; */
	background-color: rgb(0 0 0 / 20%);
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}

.default-image-container-dark {
	background-color: rgb(0 0 0 / 80%);
}

.default-image {
	width: auto !important;
	height: 85% !important;
	max-height: 185px;
	opacity: 0.3;
}

.game-image-hover {
	width: 100%;
	height: 100%;
	display: block;
	transition: transform 0.3s ease;
	object-fit: cover;
	object-position: top center;
}

.image-container:hover .game-image-hover {
	transform: scale(1.1);
}

.image-container {
	position: relative;
	width: 100%;
	overflow: hidden;
	background-color: var(--dark, #1a1a2e);
}

/* Aspect Ratio Classes */
.image-container.aspect-square {
	aspect-ratio: 1 / 1;
}

.image-container.aspect-portrait {
	aspect-ratio: 3 / 4;
}

.image-container.aspect-wide {
	aspect-ratio: 4 / 3;
}

.image-container.aspect-landscape {
	aspect-ratio: 16 / 9;
}

/* Category Ribbon Badge (TOP / NEW) */
.ribbon-badge {
	position: absolute;
	top: 0;
	left: 0;
	width: min(34px, 30%) !important;
	height: auto !important;
	z-index: 2;
	pointer-events: none;
	user-select: none;
	filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.3));
}

.image-container .overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.8);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.3s, visibility 0.3s;
}

.image-container .overlay-light {
	background-color: var(--list-gray);
}

.image-container:hover .overlay {
	/* .image-container .overlay { */
	opacity: 1;
	visibility: visible;
}

.play-button-container {
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
}

.demo-text {
	margin-top: 8px;
	color: var(--white);
	font-size: 13px;
	transition: transform 0.3s ease-in-out;
}

.demo-text-small {
	margin-top: 7px;
	font-size: 12px;
}

.demo-text-large {
	margin-top: 16px;
	font-size: 15px;
}

.demo-text:hover {
	color: rgb(204, 204, 204);
}
</style>

<style lang="scss" scoped>
.circle {
	stroke: var(--off-light-gray);
	stroke-dasharray: 650;
	stroke-dashoffset: 0;
	-webkit-transition: all 0.5s ease-in-out;
	opacity: 1;
}

.play-button svg {
	height: 40px;
	width: 40px;
}

.play-button {
	height: 40px;
	width: 40px;
	display: inline-block;
	-webkit-transition: all 0.5s ease;

	.triangle {
		-webkit-transition: all 0.7s ease-in-out;
		stroke-dasharray: 240;
		stroke-dashoffset: 0;
		stroke: var(--off-light-gray);
		transform: translateY(0) scale(1);
	}

	&:hover {

		.triangle {
			stroke-dashoffset: 480;
			opacity: 1;
			stroke: var(--off-light-gray);
			animation: nudge 0.7s ease-in-out;
			transform: translateY(0) scale(1.3);
			transform-origin: center;

			@keyframes nudge {
				0% {
					transform: translateX(0) scale(1);
				}

				30% {
					transform: translateX(-5px) scale(1);
				}

				50% {
					transform: translateX(5px);
				}

				70% {
					transform: translateX(-2px) scale(1);
				}

				100% {
					transform: translateX(0) scale(1.3);
				}
			}
		}

		.circle {
			stroke-dashoffset: 650;
			opacity: 0.3;
		}
	}
}

.play-button-extra-small {
	height: 30px;
	width: 30px;
}

.play-button-extra-small svg {
	height: 30px;
	width: 30px;
}

.play-button-small {
	height: 35px;
	width: 35px;
}

.play-button-small svg {
	height: 35px;
	width: 35px;
}

.play-button-large svg {
	height: 60px;
	width: 60px;
}

.play-button-large {
	height: 60px;
	width: 60px;
}

.favourite-container {
	position: absolute;

	background-color: var(--list-gray);
	padding: 5px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	transition: background-color 0.3s ease;
}

.favourite-container-extra-small {
	top: 0px;
	right: 0px;
}

.favourite-container .fav-svg-extra-small {
	width: 12px;
	height: 12px;
}

.favourite-container-small {
	top: 8px;
	right: 8px;
}

.favourite-container .fav-svg-small {
	width: 14px;
	height: 14px;
}

.favourite-container-large {
	top: 15px;
	right: 15px;
}

.favourite-container .fav-svg-large {
	width: 18px;
	height: 18px;
}

.favourite-container:hover svg {
	opacity: 0.8;
}

.game-limits {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: var(--list-gray);
	color: var(--white);
	// padding: 5px 10px;
	padding-top: 5px;
	padding-bottom: 5px;
	border-radius: 0 0 5px 5px;
	font-size: 12px;
	text-align: center;
	white-space: nowrap;
	z-index: 0;
}

.game-limits-small {
	font-size: 10px;
}

.table-schedule {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: rgba(0, 0, 0, 0.8);
	color: var(--white);
	padding: 5px 10px;
	border-radius: 0 0 5px 5px;
	font-size: 12px;
	text-align: center;
	white-space: nowrap;
}

.table-schedule-label {
	display: block;
	margin-top: 20%;
}

.table-schedule-time {
	display: block;
}

.game-statistics-card {
	position: absolute;
	right: 3px;
	bottom: 28px;
	z-index: 0;
	background: rgba(24, 28, 36, 0.5);
	border-radius: 12px;
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: fadeInStats 0.7s;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	max-width: 100%;
	font-size: clamp(10px, 1.7vw, 13px);
	padding: 7px 10px 6px 10px;
	pointer-events: none;
	scale: 0;
}

@keyframes fadeInStats {
	from {
		opacity: 0;
		transform: translateY(8px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.roulette-numbers {
	display: flex;
	gap: 4px;
}

.roulette-number {
	display: inline-block;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	text-align: center;
	font-weight: 600;
	font-size: 11px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
	line-height: 13px;
	background: var(--dark-gray);
	color: var(--white);
	margin: 0 1px;
	transition: transform 0.18s;
}

.roulette-green {
	background: linear-gradient(145deg, var(--primary), var(--primary));
	color: var(--white);
}

.roulette-red {
	background: linear-gradient(145deg, var(--red), var(--red-dark));
	color: var(--white);
}

.roulette-black {
	background: linear-gradient(145deg, var(--light-dark), var(--dark));
	color: var(--white);
}

.bigroad-grid {
	position: relative;
	width: 110px;
	height: 80px;
	margin-top: 2px;
}

.bigroad-cell {
	position: absolute;
	width: 11px;
	height: 11px;
	border-radius: 3px;
	background: rgba(0, 0, 0, 0.45);
	border: 2px solid var(--off-light-gray);
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
}

.bigroad-cell.banker {
	border-color: var(--red);
}

.bigroad-cell.player {
	border-color: #0090ff;
}

.bigroad-cell.tie::after {
	content: "'";
	color: var(--primary);
	font-weight: bold;
	font-size: 12px;
	position: absolute;
	right: 2px;
	top: -5px;
	z-index: 2;
	pointer-events: none;
}

.blackjack-seats {
	display: flex;
	gap: 3px;
	margin-top: 2px;
}

.seat-circle {
	display: flex;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	align-items: center;
	justify-content: center;
	background: var(--light-dark);
}

.seat-circle.taken {
	filter: grayscale(0.4) opacity(0.65);
}

.seat-circle.free {
	box-shadow: 0 0 0 2px var(--primary);
}

.seat-circle svg {
	display: block;
}

.game-statistics-card-small {
	scale: 0.7;
	right: -19px;
	bottom: 20px;
	visibility: visible;
}

.game-statistics-card-baccarat-small {
	scale: 0.5;
	right: -28px;
	bottom: -2px;
	visibility: visible;
}

.game-statistics-card-large {
	scale: 1;
}

.dealer-name-container {
	position: absolute;
	top: 3px;
	left: 3px;
	z-index: 0;
	background-color: var(--list-gray);
	color: var(--white);
	font-weight: 500;
	font-size: 13px;
	letter-spacing: 1px;
	border-radius: 22px;
	padding: 2px 11px 2px 6px;
	display: flex;
	align-items: center;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
	pointer-events: none;
	user-select: none;
}

.fs-9px {
	font-size: 9px !important;
}
</style>